export const formatTimestampUTC5 = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const offsetMillis = 5 * 60 * 60 * 1000; 
    const utcPlus5Date = new Date(date.getTime() + offsetMillis);
    
    return utcPlus5Date.toLocaleString('en-US', {
        hour12: true,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

export const toUTCPlus5 = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const offsetMillis = 5 * 60 * 60 * 1000; 
    const utcPlus5Date = new Date(date.getTime() + offsetMillis);

    return utcPlus5Date.toLocaleString('en-US', {
        hour12: true,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

export const toUTCPlus5DateOnly = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const offsetMillis = 5 * 60 * 60 * 1000; 
    const utcPlus5Date = new Date(date.getTime() + offsetMillis);
    return utcPlus5Date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const formatDueDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    
    // For due dates, we want to show the date as end of day (11:59 PM) in local timezone
    // This avoids timezone conversion issues and clearly indicates the deadline
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 0, 0);
    
    return endOfDay.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

export const toUTCPlus5Time = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const offsetMillis = 5 * 60 * 60 * 1000; 
    const utcPlus5Date = new Date(date.getTime() + offsetMillis);
    
    return utcPlus5Date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
    });
};

export const toUTCPlus5DateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const offsetMillis = 5 * 60 * 60 * 1000; 
    const utcPlus5Date = new Date(date.getTime() + offsetMillis);
    
    return utcPlus5Date.toLocaleString('en-US', {
        hour12: true,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

/**
 * Parse API timestamps as UTC when timezone is missing (Mongo/FastAPI naive ISO),
 * then display helpers use Asia/Karachi (PKT / GMT+5).
 */
export const parseApiDate = (dateString) => {
    if (!dateString) return null;
    let raw = String(dateString).trim();
    if (!raw) return null;
    // Treat naive ISO strings as UTC
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
        raw = `${raw}Z`;
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date;
};

/**
 * Convert a date string to a PKT (UTC+5) Date object suitable for date-fns.
 * date-fns format/isToday/etc. work off the Date's local epoch millis,
 * so we shift by +5h from the UTC instant to get the correct wall-clock representation.
 */
export const toPKTDate = (dateString) => {
    const date = parseApiDate(dateString);
    if (!date) return null;
    return new Date(date.getTime() + 5 * 60 * 60 * 1000);
};

export const formatPKTShortTime = (dateString) => {
    const date = parseApiDate(dateString);
    if (!date) return '';
    return date.toLocaleTimeString('en-US', {
        timeZone: 'Asia/Karachi',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

export const formatPKTTime12h = (dateString) => {
    const date = parseApiDate(dateString);
    if (!date) return '';
    return date.toLocaleTimeString('en-US', {
        timeZone: 'Asia/Karachi',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

/**
 * Calendar day key (YYYY-MM-DD) in Asia/Karachi (PKT / GMT+5).
 * Use for timesheet day columns so entries land on the correct PKT day
 * regardless of the browser's local timezone.
 */
export const toPKTDayKey = (dateString) => {
    const date = parseApiDate(dateString);
    if (!date) return null;
    return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
};

/**
 * Short PKT date label for expanded timesheet rows (e.g. "Sun, Jul 19").
 */
export const formatPKTDayLabel = (dateString) => {
    const date = parseApiDate(dateString);
    if (!date) return '—';
    return date.toLocaleDateString('en-US', {
        timeZone: 'Asia/Karachi',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
};

export const formatDurationHMS = (totalSeconds) => {
    if (!totalSeconds && totalSeconds !== 0) return '—';

    const seconds = Math.floor(totalSeconds);
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hrs}h ${mins}m ${secs}s`;
};
